"use client";

import { c as _c } from "react/compiler-runtime";
import type { ColumnDefinition } from "@xefi/x-react/datagrid";
import { IconHistory, IconPencil } from "@xefi/x-react/icons";
import { useTranslation } from "react-i18next";
import type { MissingCardPaymentModel } from "../../../../domain/models/MissingCardPayment";
import { usePermissions } from "@/hooks/usePermissions";
import { CellTooltip } from "../common/CellTooltip";
import { formatPaymentDate, formatStringAmount, formatText, isArchived, isDone } from "../common/paymentsFormat";
import type { EditableField } from "./MissingCardPaymentRowEditor";
type EditControls = {
  onOpenEditor: (row: MissingCardPaymentModel, field: EditableField, anchor: Element) => void;
  onOpenHistory: (row: MissingCardPaymentModel) => void;
};
const WRAPPED_CELL = "h-auto max-h-none whitespace-normal py-1.5 align-top";
const WRAPPED_TEXT = "line-clamp-3 whitespace-normal text-xs leading-snug text-black dark:text-white";
export const useMissingCardPaymentsColumns = t0 => {
  const $ = _c(45);
  const {
    onOpenEditor,
    onOpenHistory
  } = t0;
  const {
    t
  } = useTranslation();
  const {
    hasRole
  } = usePermissions();
  let t1;
  if ($[0] !== t) {
    t1 = field => t(`paymentsReceived.missingCardPayments.columns.${field}`);
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const label = t1;
  const textCell = _temp;
  const editableCell = _temp2;
  let t2;
  if ($[2] !== label || $[3] !== onOpenEditor) {
    t2 = (field_0, widthClass, display, t3) => {
      const bold_0 = t3 === undefined ? false : t3;
      return {
        header: label(field_0),
        field: field_0,
        sortable: true,
        className: widthClass,
        cellClassName: _temp3,
        onClick: (row_0, event) => {
          if (isArchived(row_0)) {
            return;
          }
          onOpenEditor(row_0, field_0, event.currentTarget);
        },
        cell: row_1 => editableCell(display(row_1), bold_0, !isArchived(row_1))
      };
    };
    $[2] = label;
    $[3] = onOpenEditor;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const buildEditableColumn = t2;
  let t3;
  if ($[5] !== label) {
    t3 = (field_1, widthClass_0, render, t4) => {
      const sortable = t4 === undefined ? true : t4;
      return {
        header: label(field_1),
        field: field_1,
        sortable,
        className: widthClass_0,
        cell: row_2 => textCell(render(row_2))
      };
    };
    $[5] = label;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const buildReadonlyColumn = t3;
  let t4;
  if ($[7] !== t) {
    t4 = row_3 => isDone(row_3.to_do) ? t("paymentsReceived.missingCardPayments.yes") : t("paymentsReceived.missingCardPayments.no");
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const doneLabel = t4;
  let t5;
  if ($[9] !== buildEditableColumn || $[10] !== buildReadonlyColumn || $[11] !== doneLabel || $[12] !== hasRole) {
    t5 = !hasRole(["Applicant", "Viewer"]) ? buildEditableColumn("to_do", "w-[7%]", doneLabel, true) : buildReadonlyColumn("to_do", "w-[7%]", doneLabel);
    $[9] = buildEditableColumn;
    $[10] = buildReadonlyColumn;
    $[11] = doneLabel;
    $[12] = hasRole;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const toDoColumn = t5;
  let t10;
  let t11;
  let t12;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[14] !== buildReadonlyColumn) {
    t6 = buildReadonlyColumn("document_date", "w-[8%]", _temp4);
    t7 = buildReadonlyColumn("amount", "w-[8%]", _temp5);
    t8 = buildReadonlyColumn("label", "w-[21%]", _temp6);
    t9 = buildReadonlyColumn("ref_key_1", "w-[7%]", _temp7);
    t10 = buildReadonlyColumn("ref_key_2", "w-[8%]", _temp8);
    t11 = buildReadonlyColumn("ref_key_3", "w-[7%]", _temp9);
    t12 = buildReadonlyColumn("functional_doma", "w-[7%]", _temp10);
    $[14] = buildReadonlyColumn;
    $[15] = t10;
    $[16] = t11;
    $[17] = t12;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t10 = $[15];
    t11 = $[16];
    t12 = $[17];
    t6 = $[18];
    t7 = $[19];
    t8 = $[20];
    t9 = $[21];
  }
  let t13;
  let t14;
  if ($[22] !== buildEditableColumn) {
    t13 = buildEditableColumn("assignment", "w-[8%]", _temp11);
    t14 = buildEditableColumn("comment", "w-[13%]", _temp12);
    $[22] = buildEditableColumn;
    $[23] = t13;
    $[24] = t14;
  } else {
    t13 = $[23];
    t14 = $[24];
  }
  let t15;
  if ($[25] !== label) {
    t15 = label("last_action");
    $[25] = label;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== onOpenHistory || $[28] !== t) {
    t16 = row_13 => <div className="flex justify-center"><button type="button" aria-label={t("paymentsReceived.actions.history")} title={t("paymentsReceived.actions.history")} className="text-[#7d828a] transition-colors hover:text-primary dark:text-white" onClick={() => onOpenHistory(row_13)}><IconHistory size={18} /></button></div>;
    $[27] = onOpenHistory;
    $[28] = t;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== t15 || $[31] !== t16) {
    t17 = {
      header: t15,
      field: "actions",
      sortable: false,
      className: "w-[6%]",
      cell: t16
    };
    $[30] = t15;
    $[31] = t16;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== t10 || $[34] !== t11 || $[35] !== t12 || $[36] !== t13 || $[37] !== t14 || $[38] !== t17 || $[39] !== t6 || $[40] !== t7 || $[41] !== t8 || $[42] !== t9 || $[43] !== toDoColumn) {
    t18 = [t6, t7, t8, t9, t10, t11, t12, t13, t14, toDoColumn, t17];
    $[33] = t10;
    $[34] = t11;
    $[35] = t12;
    $[36] = t13;
    $[37] = t14;
    $[38] = t17;
    $[39] = t6;
    $[40] = t7;
    $[41] = t8;
    $[42] = t9;
    $[43] = toDoColumn;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  return t18;
};
function _temp(value) {
  return <CellTooltip value={value}><span className="block truncate text-xs text-black dark:text-white">{value}</span></CellTooltip>;
}
function _temp2(value_0, bold, isEditable) {
  return <CellTooltip value={value_0}><span className="flex w-full items-start gap-1"><span className={`min-w-0 flex-1 ${WRAPPED_TEXT} ${bold ? "font-bold" : ""}`}>{value_0}</span>{isEditable && <IconPencil size={16} className="shrink-0 text-danger/70" aria-hidden={true} />}</span></CellTooltip>;
}
function _temp3(row) {
  return isArchived(row) ? WRAPPED_CELL : `cursor-pointer bg-danger/5 transition-colors hover:bg-danger/15 ${WRAPPED_CELL}`;
}
function _temp4(row_4) {
  return formatPaymentDate(row_4.document_date);
}
function _temp5(row_5) {
  return `${formatStringAmount(row_5.amount)} €`;
}
function _temp6(row_6) {
  return formatText(row_6.label);
}
function _temp7(row_7) {
  return formatText(row_7.ref_key_1);
}
function _temp8(row_8) {
  return formatText(row_8.ref_key_2);
}
function _temp9(row_9) {
  return formatText(row_9.ref_key_3);
}
function _temp10(row_10) {
  return formatText(row_10.functional_doma);
}
function _temp11(row_11) {
  return formatText(row_11.assignment);
}
function _temp12(row_12) {
  return formatText(row_12.comment);
}